import type { MouseEvent, RefObject } from "react";
import { useCallback, useRef, useState } from "react";

type UseGenieTransformOriginResult = {
    positionerRef: RefObject<HTMLDivElement | null>;
    transformOrigin: string | undefined;
    captureOrigin: (event?: MouseEvent<HTMLElement>) => void;
};

/**
 * Captures the point a genie/scale overlay should emerge from. The returned `positionerRef`
 * MUST be attached to a `position: relative` ancestor of the overlay; `captureOrigin` reads the
 * clicked element's center relative to that positioner and stores it as a CSS `transform-origin`.
 */
export const useGenieTransformOrigin = (): UseGenieTransformOriginResult => {
    const positionerRef = useRef<HTMLDivElement>(null);
    const [transformOrigin, setTransformOrigin] = useState<string | undefined>(undefined);

    const captureOrigin = useCallback((event?: MouseEvent<HTMLElement>) => {
        const positioner = positionerRef.current;
        const target = event?.currentTarget;

        if (!positioner || !target) {
            return;
        }

        const positionerRect = positioner.getBoundingClientRect();
        const buttonRect = target.getBoundingClientRect();

        const originX = buttonRect.left - positionerRect.left + buttonRect.width / 2;
        const originY = buttonRect.top - positionerRect.top + buttonRect.height / 2;

        setTransformOrigin(`${originX}px ${originY}px`);
    }, []);

    return { positionerRef, transformOrigin, captureOrigin };
};
